<script lang="ts">
	//Svelte
	import { format } from 'svelte-i18n';
	import { push } from 'svelte-spa-router';
	//Stores
	import { user_settings, user_data, profile_data } from 'src/stores/account/user';
	import { edit } from 'src/stores/ui/edit_lists';
	//Helpers
	import { getTimeFormat } from 'src/helpers/date_helper';
	import { weather_store } from 'src/helpers/geolocalion_helper';
	import { celsius_to_fahrenheit } from 'src/helpers/interface_helpers';
	import { formatTitleToSnakeCase } from 'src/helpers/utilities';
	import { isSubAccount } from 'src/helpers/user_helpers';
	//Design system components
	import { Button, DashWeatherWidget, LabelV2 } from '@Shelly/shelly-design-system';
	//Components
	import WeatherLocationModal from 'src/components/Weather/WeatherLocationModal.svelte';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import { sidepanelDesktopOnTop } from 'src/stores/ui/sidepanel-public';
	let showWeatherLocationModal = false;

	$: temperature =
		String($profile_data.weather_temp_unit) === 'F'
			? `${celsius_to_fahrenheit($weather_store?.temperature.split('°')[0]) || 0}°`
			: $weather_store?.temperature;

	$: temperaturePrefix = String($profile_data.weather_temp_unit) === 'F' ? 'F' : 'C';

	$: timeDetails = [...($weather_store?.forecast || [])].map((forecast) => {
		const time = forecast.time.toString().split(' ')[4];
		const temp =
			String($profile_data.weather_temp_unit) === 'F'
				? `${celsius_to_fahrenheit(forecast.temperature.split('°')[0])}`
				: `${forecast.temperature.split('°')[0]}°`;

		forecast = {
			title: getTimeFormat(time.split(':')[0], time.split(':')[1], false),
			icon: forecast.icon,
			text: temp,
			precipitation: forecast.pop,
		};

		return forecast;
	});

	const goToPremiumSettings = () => {
		push('/settings/premium');
		if (!$sidepanelDesktopOnTop) {
			setSidePanelState({ open: false });
		}
	};

	const windDirectionIconsMap = {
		N: 'fa-light fa-arrow-up',
		NNE: 'fa-light fa-arrow-up-right',
		NE: 'fa-light fa-arrow-up-right',
		ENE: 'fa-light fa-arrow-up-right',
		E: 'fa-light fa-arrow-right',
		ESE: 'fa-light fa-arrow-down-right',
		SE: 'fa-light fa-arrow-down-right',
		SSE: 'fa-light fa-arrow-down-right',
		S: 'fa-light fa-arrow-down',
		SSW: 'fa-light fa-arrow-down-left',
		SW: 'fa-light fa-arrow-down-left',
		WSW: 'fa-light fa-arrow-down-left',
		W: 'fa-light fa-arrow-left',
		WNW: 'fa-light fa-arrow-up-left',
		NW: 'fa-light fa-arrow-up-left',
		NNW: 'fa-light fa-arrow-up-left',
	};
</script>

{#if !$user_data?.premium}
	<DashWeatherWidget aria-label="Sofia Weather Widget" image={'/images/weather/Cloudy.png'}>
		<LabelV2
			slot="leftBadge"
			size="s"
			icon="fal fa-gem"
			backgroundColor="--gradient-premium"
			value={$format('label_premium', { default: 'Premium' })}
			noRadiusType="second"
		/>

		<Button
			variant="premium"
			class="text-capitalize"
			size="m"
			on:click={goToPremiumSettings}
			disabled={$edit}
		>
			{$format('button_get_premium_weather', {
				default: 'get premium for weather widget',
			})}
		</Button>
	</DashWeatherWidget>
{:else if $user_data.premium && $user_settings?.weather_station?.data?.stationName}
	<!-- if weather station is set & you have weather store info -->
	{#if $weather_store}
		<DashWeatherWidget
			aria-label="Weather Widget"
			a11yProps={{ temperatureA11yText: `Temperature is currently ${temperature} °C` }}
			{temperature}
			action={{
				icon: 'fas fa-pen',
				title: `${$user_settings?.weather_station?.data?.stationName} / °${temperaturePrefix}`,
				isClickable: !$edit,
			}}
			{temperaturePrefix}
			image={`./images/weather/${$weather_store.image}`}
			currentDayDetails={{
				icon: $weather_store.icon,
				type: $format(`weather_widget_type_${formatTitleToSnakeCase($weather_store.type)}`, {
					default: $weather_store.type,
				}),
				details: [
					{
						title: $format('weather_widget_precipitation', {
							default: 'Precipitation',
						}),
						text: $weather_store.pop,
					},
					{
						title: $format('weather_widget_humidity', { default: 'Humidity' }),
						text: $weather_store.humidity,
					},
					{
						title: $format('weather_widget_wind', { default: 'Wind' }),
						text: `${$weather_store.wind.split(' ')[0]} ${$weather_store.wind.split(' ')[1]}`,
						icon:
							Number($weather_store?.wind.split(' ')[0]) > 0
								? windDirectionIconsMap[$weather_store?.wind.split(' ')[2]]
								: '',
					},
				],
			}}
			{timeDetails}
			hideContentSlot={$edit}
			on:edit-click={() => {
				if (!isSubAccount()) {
					showWeatherLocationModal = true;
				}
			}}
		/>
	{/if}
{:else if $user_data.premium}
	<!-- if weather station is is not set -->
	<DashWeatherWidget image="./images/weather/Cloudy.png">
		<LabelV2
			slot="leftBadge"
			size="s"
			icon="fal fa-gem"
			backgroundColor="--gradient-premium"
			value={$format('premium', { default: 'Premium' })}
			noRadiusType="second"
		/>

		<Button
			variant="premium"
			class="text-capitalize"
			size="m"
			disabled={$edit}
			on:click={() => (showWeatherLocationModal = true)}
		>
			{$format('button_add_weather_widget', {
				default: 'Add Weather Widget',
			})}
		</Button>
	</DashWeatherWidget>
{/if}

{#if showWeatherLocationModal}
	<WeatherLocationModal bind:showModal={showWeatherLocationModal} />
{/if}
